import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';

const Payment = () => {
  const { items: cartItems, totalAmount, clearCart } = useCart();
  const { success: showToastSuccess, error: showToastError } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  
  const [addresses, setAddresses] = useState([]);

  const [newAddress, setNewAddress] = useState({
    name: 'Home',
    fullName: '',
    phone: '+91 ',
    address: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    type: 'home',
    isDefault: false
  });

  const [showAddAddress, setShowAddAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const result = await api.get('/auth/profile');
        if (result.success && result.data) {
          const user = result.data;
          // Create initial address from user profile if available
          if (user.address) {
            const initialAddress = {
              id: 'profile',
              name: 'Profile Address',
              fullName: user.name,
              phone: user.phone || '',
              address: user.address,
              landmark: '',
              city: user.city || '',
              state: user.state || '',
              pincode: user.pincode || '',
              type: 'home',
              isDefault: true
            };
            setAddresses([initialAddress]);
            setSelectedAddress(initialAddress);
          }
          
          setNewAddress(prev => ({
            ...prev,
            fullName: user.name,
            phone: user.phone || '+91 '
          }));
        }
      } catch (err) {
        console.error('Error fetching profile for address:', err);
      }
    };
    fetchProfile();
  }, []);

  const paymentMethods = [
    {
      id: 'upi',
      name: 'UPI',
      description: 'Pay using UPI apps like GPay, PhonePe, Paytm',
      icon: 'fas fa-mobile-alt',
      color: '#00D4AA'
    },
    {
      id: 'card',
      name: 'Credit/Debit Card',
      description: 'Visa, Mastercard, Rupay, American Express',
      icon: 'fas fa-credit-card',
      color: '#4A90E2'
    },
    {
      id: 'cod',
      name: 'Cash on Delivery',
      description: 'Pay when you receive your order',
      icon: 'fas fa-money-bill-wave',
      color: '#4CAF50'
    }
  ];

  const calculateSubtotal = () => {
    return totalAmount;
  };

  const calculateMRPSavings = () => {
    return cartItems.reduce((total, item) => {
      const originalPrice = item.originalPrice || item.price;
      return total + ((originalPrice - item.price) * item.quantity);
    }, 0);
  };

  const calculateShipping = () => {
    return calculateSubtotal() > 700 || calculateSubtotal() === 0 ? 0 : 79;
  };

  const calculateTax = () => {
    return 0;
  };

  const calculateTotal = () => {
    const total = calculateSubtotal() + calculateShipping() + calculateTax() - discountAmount;
    return total > 0 ? total : 0;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode) {
      showToastError('Please enter a coupon code');
      return;
    }

    setIsApplyingCoupon(true);
    try {
      const result = await api.post('/coupons/validate', {
        code: couponCode,
        orderAmount: calculateSubtotal()
      });

      if (result.success) {
        setDiscountAmount(result.data.discountAmount);
        setAppliedCoupon(result.data.couponCode);
        showToastSuccess(`Coupon applied! You saved ₹${result.data.discountAmount}`);
      } else {
        showToastError(result.message || 'Invalid coupon code');
        setDiscountAmount(0);
        setAppliedCoupon(null);
      }
    } catch (err) {
      showToastError(err.message || 'Failed to apply coupon');
      setDiscountAmount(0);
      setAppliedCoupon(null);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setDiscountAmount(0);
    setAppliedCoupon(null);
    showToastSuccess('Coupon removed');
  };

  const validatePincode = (pincode) => {
    return /^\d{6}$/.test(pincode);
  };

  const handleCancel = () => {
    setShowAddAddress(false);
    setEditingAddressId(null);
    setNewAddress({
      name: 'Home',
      fullName: '',
      phone: '+91 ',
      address: '',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      type: 'home',
      isDefault: false
    });
  };

  const handleAddAddress = async () => {
    if (!validatePincode(newAddress.pincode)) {
      showToastError('Please enter a valid 6-digit pincode');
      return;
    }

    let phoneVal = newAddress.phone.trim();
    // If it's a 10-digit local number, prepend +91
    if (/^\d{10}$/.test(phoneVal)) {
      phoneVal = `+91 ${phoneVal}`;
    }

    // Now validate the length of local part (without +91 prefix)
    const cleanPhone = phoneVal.replace(/\D/g, '');
    let cleanLocal = cleanPhone;
    if (cleanPhone.startsWith('91')) {
      cleanLocal = cleanPhone.substring(2);
    }
    if (cleanLocal.length < 10) {
      showToastError('Please enter a valid 10-digit phone number');
      return;
    }

    const address = {
      ...newAddress,
      phone: phoneVal,
      id: editingAddressId || Date.now()
    };

    if (editingAddressId) {
      const updatedAddresses = addresses.map(addr => {
        if (addr.id === editingAddressId) {
          return address;
        }
        return addr;
      });
      setAddresses(updatedAddresses);
      if (selectedAddress?.id === editingAddressId) {
        setSelectedAddress(address);
      }
      setEditingAddressId(null);
      setShowAddAddress(false);
      showToastSuccess('Address updated successfully');
    } else {
      setAddresses([...addresses, address]);
      setSelectedAddress(address);
      setShowAddAddress(false);
      showToastSuccess('Address added successfully');
    }

    try {
      await api.patch('/auth/profile', {
        phone: phoneVal,
        address: newAddress.address,
        city: newAddress.city,
        state: newAddress.state,
        pincode: newAddress.pincode
      });
      showToastSuccess('Address details saved to your profile!');
    } catch (err) {
      console.error('Failed to auto-save address to user profile:', err);
    }
  };

  const handlePayment = async () => {
    if (!selectedAddress) {
      showToastError('Please select a delivery address');
      return;
    }

    if (!paymentMethod) {
      showToastError('Please select a payment method');
      return;
    }

    setIsProcessing(true);

    try {
      if (paymentMethod === 'cod') {
        // For COD, we still use the existing backend order creation route
        const paymentIntentId = `cod_${Date.now()}`;
        const orderResult = await api.post('/orders/create', {
          paymentIntentId,
          shippingAddress: selectedAddress,
          paymentMethod,
          couponCode: appliedCoupon || couponCode
        });

        if (orderResult.success) {
          setOrderPlaced(true);
          clearCart();
          showToastSuccess('Order placed successfully!');
        } else {
          throw new Error(orderResult.message || 'Failed to place order');
        }
      } else {
        // Cashfree checkout flow for online payments
        const returnUrl = `${window.location.origin}/checkout`;
        const orderResult = await api.post('/payments/create-order', {
          couponCode,
          shippingAddress: selectedAddress,
          returnUrl
        });

        if (!orderResult.success) {
          throw new Error(orderResult.message || 'Failed to initialize payment session');
        }

        const { paymentSessionId, orderId } = orderResult.data;

        if (paymentSessionId.startsWith('mock_cf_session_')) {
          window.location.href = `${returnUrl}?order_id=${orderId || orderResult.data.cfOrderId}`;
          return;
        }

        const mode = process.env.REACT_APP_CASHFREE_MODE || 'sandbox';
        
        if (window.Cashfree) {
          const cashfree = window.Cashfree({
            mode: mode.toLowerCase() === 'production' ? 'production' : 'sandbox'
          });
          cashfree.checkout({
            paymentSessionId,
            redirectTarget: '_self'
          });
        } else {
          // Fallback to manual redirect if Cashfree SDK script is not loaded
          const baseUrl = mode.toLowerCase() === 'production'
            ? 'https://payments.cashfree.com/pg/view/checkout'
            : 'https://sandbox.cashfree.com/pg/view/checkout';
          window.location.href = `${baseUrl}?session_id=${paymentSessionId}`;
        }
      }
    } catch (err) {
      console.error('Checkout error:', err);
      showToastError(err.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="payment-page">
      <div className="container">
        <div className="payment-header">
          <h1>Checkout</h1>
          <div className="progress-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">Address</div>
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-label">Payment</div>
            </div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <div className="step-label">Confirm</div>
            </div>
          </div>
        </div>

        {!orderPlaced ? (
          <div className="payment-content">
            <div className="payment-main">
              {/* Step 1: Address Selection */}
              {currentStep === 1 && (
                <div className="payment-section">
                  <h2>Select Delivery Address</h2>
                  
                  <div className="addresses-list">
                    {addresses.map(address => (
                      <div 
                        key={address.id}
                        className={`address-card ${selectedAddress?.id === address.id ? 'selected' : ''}`}
                        onClick={() => setSelectedAddress(address)}
                      >
                        <div className="address-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div className="address-type">
                            <i className={`fas fa-${address.type === 'home' ? 'home' : 'briefcase'}`}></i>
                            <span>{address.name}</span>
                            {address.isDefault && <span className="default-badge">Default</span>}
                          </div>
                          <button 
                            className="btn-edit-address"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingAddressId(address.id);
                              setNewAddress({
                                name: address.name || 'Home',
                                fullName: address.fullName,
                                phone: address.phone,
                                address: address.address,
                                landmark: address.landmark || '',
                                city: address.city,
                                state: address.state,
                                pincode: address.pincode,
                                type: address.type || 'home',
                                isDefault: address.isDefault || false
                              });
                              setShowAddAddress(true);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#2f4a67',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.375rem',
                              transition: 'all 0.2s'
                            }}
                          >
                            <i className="fas fa-edit"></i> Edit
                          </button>
                        </div>
                        
                        <div className="address-details">
                          <p className="address-name">{address.fullName}</p>
                          <p className="address-phone">{address.phone}</p>
                          <p className="address-text">
                            {address.address}, {address.landmark && `${address.landmark}, `}
                            {address.city}, {address.state} - {address.pincode}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button 
                    className="btn-add-address"
                    onClick={() => setShowAddAddress(true)}
                  >
                    <i className="fas fa-plus"></i> Add New Address
                  </button>

                  {showAddAddress && (
                    <div className="add-address-form">
                      <h3>{editingAddressId ? 'Edit Address' : 'Add New Address'}</h3>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Full Name *</label>
                          <input
                            type="text"
                            value={newAddress.fullName}
                            onChange={(e) => setNewAddress({...newAddress, fullName: e.target.value})}
                            placeholder="Your full name"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Phone Number *</label>
                          <input
                            type="tel"
                            value={newAddress.phone}
                            onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                            placeholder="+91 98765 43210"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Address *</label>
                          <input
                            type="text"
                            value={newAddress.address}
                            onChange={(e) => setNewAddress({...newAddress, address: e.target.value})}
                            placeholder="Street address, apartment, etc."
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Landmark</label>
                          <input
                            type="text"
                            value={newAddress.landmark}
                            onChange={(e) => setNewAddress({...newAddress, landmark: e.target.value})}
                            placeholder="Nearby landmark"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>City *</label>
                          <input
                            type="text"
                            value={newAddress.city}
                            onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                            placeholder="City name"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>State *</label>
                          <input
                            type="text"
                            value={newAddress.state}
                            onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                            placeholder="State name"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Pincode *</label>
                          <input
                            type="text"
                            value={newAddress.pincode}
                            onChange={(e) => setNewAddress({...newAddress, pincode: e.target.value})}
                            placeholder="6-digit pincode"
                            maxLength={6}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-actions">
                        <button 
                          className="btn-secondary"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button className="btn-primary" onClick={handleAddAddress}>
                          {editingAddressId ? 'Save Changes' : 'Add Address'}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="step-navigation">
                    <button 
                      className="btn-primary"
                      onClick={() => setCurrentStep(2)}
                      disabled={!selectedAddress}
                    >
                      Continue to Payment <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Payment Method */}
              {currentStep === 2 && (
                <div className="payment-section">
                  <h2>Select Payment Method</h2>
                  
                  <div className="payment-methods">
                    {paymentMethods.map(method => (
                      <div 
                        key={method.id}
                        className={`payment-method-card ${paymentMethod === method.id ? 'selected' : ''}`}
                        onClick={() => setPaymentMethod(method.id)}
                      >
                        <div className="payment-method-icon" style={{ backgroundColor: method.color }}>
                          <i className={method.icon}></i>
                        </div>
                        <div className="payment-method-info">
                          <h3>{method.name}</h3>
                          <p>{method.description}</p>
                        </div>
                        <div className="payment-method-radio">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={method.id}
                            checked={paymentMethod === method.id}
                            onChange={() => setPaymentMethod(method.id)}
                          />
                          <span className="radio-custom"></span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="step-navigation">
                    <button className="btn-secondary" onClick={() => setCurrentStep(1)}>
                      <i className="fas fa-arrow-left"></i> Back to Address
                    </button>
                    <button 
                      className="btn-primary"
                      onClick={() => setCurrentStep(3)}
                      disabled={!paymentMethod}
                    >
                      Review Order <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Order Confirmation */}
              {currentStep === 3 && (
                <div className="payment-section">
                  <h2>Order Confirmation</h2>
                  
                  <div className="order-summary">
                    <div className="summary-section">
                      <h3>Delivery Address</h3>
                      <div className="selected-address">
                        <p><strong>{selectedAddress.fullName}</strong></p>
                        <p>{selectedAddress.phone}</p>
                        <p>{selectedAddress.address}, {selectedAddress.landmark && `${selectedAddress.landmark}, `}</p>
                        <p>{selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}</p>
                      </div>
                    </div>

                    <div className="summary-section">
                      <h3>Payment Method</h3>
                      <div className="selected-payment">
                        <div className="payment-method-icon" style={{ 
                          backgroundColor: paymentMethods.find(m => m.id === paymentMethod)?.color 
                        }}>
                          <i className={paymentMethods.find(m => m.id === paymentMethod)?.icon}></i>
                        </div>
                        <span>{paymentMethods.find(m => m.id === paymentMethod)?.name}</span>
                      </div>
                    </div>

                    <div className="summary-section">
                      <h3>Order Items</h3>
                      <div className="order-items">
                        {cartItems.map(item => (
                          <div key={item.id} className="order-item">
                            <img src={item.image} alt={item.name} />
                            <div className="item-details">
                              <h4>{item.name}</h4>
                              <p>Size: {item.size} | Color: {item.color}{item.fabricName ? ` | Fabric: ${item.fabricName}` : ''}</p>
                              <p>Qty: {item.quantity}</p>
                            </div>
                            <div className="item-price">
                              ₹{item.price * item.quantity}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="step-navigation">
                    <button className="btn-secondary" onClick={() => setCurrentStep(2)}>
                      <i className="fas fa-arrow-left"></i> Back to Payment
                    </button>
                    <button 
                      className="btn-primary btn-large"
                      onClick={handlePayment}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> {paymentMethod === 'cod' ? 'Placing Order...' : 'Processing Payment...'}
                        </>
                      ) : (
                        <>
                          <i className={`fas fa-${paymentMethod === 'cod' ? 'check-circle' : 'lock'}`}></i> 
                          {paymentMethod === 'cod' ? ' Confirm Order' : ` Pay ₹${calculateTotal()}`}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="order-summary-sidebar">
              <h3>Order Summary</h3>
              <div className="summary-items">
                {cartItems.map(item => (
                  <div key={item.id} className="summary-item">
                    <img src={item.image} alt={item.name} />
                    <div className="summary-item-details">
                      <h4>{item.name}</h4>
                      <p>Qty: {item.quantity} | Size: {item.size}{item.fabricName ? ` | Fabric: ${item.fabricName}` : ''}</p>
                    </div>
                    <div className="summary-item-price">
                      ₹{item.price * item.quantity}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="price-breakdown">
                <div className="price-row">
                  <span>Subtotal</span>
                  <span>₹{calculateSubtotal()}</span>
                </div>
                {calculateMRPSavings() > 0 && (
                  <div className="price-row discount">
                    <span>Product Savings (MRP)</span>
                    <span style={{ color: '#22c55e', fontWeight: '600' }}>-₹{calculateMRPSavings()}</span>
                  </div>
                )}
                <div className="price-row">
                  <span>Shipping</span>
                  <span>{calculateShipping() === 0 ? 'FREE' : `₹${calculateShipping()}`}</span>
                </div>
                {calculateTax() > 0 && (
                  <div className="price-row">
                    <span>Tax (18%)</span>
                    <span>₹{calculateTax()}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="price-row discount">
                    <span>Discount</span>
                    <span>-₹{discountAmount}</span>
                  </div>
                )}
                <div className="price-row total">
                  <span>Total</span>
                  <span>₹{calculateTotal()}</span>
                </div>
              </div>

              <div className="coupon-section">
                <h4>Have a Coupon?</h4>
                <div className="coupon-input-wrapper">
                  <input 
                    type="text" 
                    placeholder="Enter coupon code" 
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    disabled={appliedCoupon}
                  />
                  {appliedCoupon ? (
                    <button className="btn-remove-coupon" onClick={removeCoupon}>Remove</button>
                  ) : (
                    <button 
                      className="btn-apply-coupon" 
                      onClick={handleApplyCoupon}
                      disabled={isApplyingCoupon || !couponCode}
                    >
                      {isApplyingCoupon ? '...' : 'Apply'}
                    </button>
                  )}
                </div>
                {appliedCoupon && (
                  <div className="applied-coupon-info">
                    <i className="fas fa-check-circle"></i>
                    <span>Code <strong>{appliedCoupon}</strong> applied!</span>
                  </div>
                )}
              </div>

              <div className="secure-payment">
                <i className="fas fa-shield-alt"></i>
                <span>Secure Payment</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="order-success">
            <div className="success-content">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h2>Order Placed Successfully!</h2>
              <p>Thank you for your order. We'll send you order confirmation and tracking details shortly.</p>
              
              <button className="btn-primary" onClick={() => window.location.href = '/'}>
                <i className="fas fa-shopping-cart"></i> Continue Shopping
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .payment-page {
          padding: 2rem 0;
          min-height: 100vh;
          background: var(--bg-secondary, #f8fafc);
        }

        .payment-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .payment-header h1 {
          font-size: 2.5rem;
          color: var(--text-primary, #1e293b);
          margin-bottom: 1rem;
        }

        .progress-indicator {
          display: flex;
          justify-content: center;
          gap: 3rem;
          margin-top: 2rem;
        }

        .step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .step-number {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--gray-200, #e2e8f0);
          color: var(--text-secondary, #64748b);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          transition: all var(--transition-normal);
        }

        .step.active .step-number {
          background: var(--gradient-primary, linear-gradient(135deg, #2f4a67, #23394f));
          color: white;
        }

        .step-label {
          font-size: 0.875rem;
          color: var(--text-secondary, #64748b);
          font-weight: 500;
        }

        .step.active .step-label {
          color: var(--primary-color, #2f4a67);
        }

        .payment-content {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 2rem;
        }

        .payment-main {
          background: var(--bg-primary, white);
          border-radius: var(--radius-2xl, 1.5rem);
          padding: 2rem;
          box-shadow: var(--shadow-lg, 0 10px 15px -3px rgb(0 0 0 / 0.1));
        }

        .payment-section h2 {
          color: var(--text-primary, #1e293b);
          margin-bottom: 2rem;
        }

        .addresses-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .address-card {
          border: 2px solid var(--border-color, #e2e8f0);
          border-radius: var(--radius-xl, 1rem);
          padding: 1.5rem;
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .address-card:hover {
          border-color: var(--primary-color, #2f4a67);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1));
        }

        .address-card.selected {
          border-color: var(--primary-color, #2f4a67);
          background: var(--gray-50, #f8fafc);
        }

        .address-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .address-type {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: var(--text-primary, #1e293b);
        }

        .default-badge {
          background: var(--success-color, #16a34a);
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius-full, 9999px);
          font-size: 0.75rem;
          font-weight: 600;
        }

        .address-details p {
          margin-bottom: 0.5rem;
          color: var(--text-secondary, #64748b);
        }

        .address-name {
          font-weight: 600;
          color: var(--text-primary, #1e293b);
        }

        .btn-add-address {
          width: 100%;
          background: transparent;
          border: 2px dashed var(--primary-color, #2f4a67);
          color: var(--primary-color, #2f4a67);
          padding: 1rem;
          border-radius: var(--radius-lg, 0.75rem);
          font-weight: 600;
          cursor: pointer;
          transition: all var(--transition-fast);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .btn-add-address:hover {
          background: var(--primary-color, #2f4a67);
          color: white;
        }

        .add-address-form {
          background: var(--gray-50, #f8fafc);
          border-radius: var(--radius-xl, 1rem);
          padding: 2rem;
          margin-top: 2rem;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          font-weight: 500;
          color: var(--text-primary, #1e293b);
          margin-bottom: 0.5rem;
        }

        .form-group input {
          padding: 0.75rem;
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: var(--radius-lg, 0.75rem);
          font-size: 0.875rem;
          transition: all var(--transition-fast);
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--primary-color, #2f4a67);
          box-shadow: 0 0 0 3px rgba(47, 74, 103, 0.18);
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .payment-methods {
          display: grid;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .payment-method-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          border: 2px solid var(--border-color, #e2e8f0);
          border-radius: var(--radius-xl, 1rem);
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .payment-method-card:hover {
          border-color: var(--primary-color, #2f4a67);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1));
        }

        .payment-method-card.selected {
          border-color: var(--primary-color, #2f4a67);
          background: var(--gray-50, #f8fafc);
        }

        .payment-method-icon {
          width: 50px;
          height: 50px;
          border-radius: var(--radius-lg, 0.75rem);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
        }

        .payment-method-info h3 {
          font-size: 1.125rem;
          color: var(--text-primary, #1e293b);
          margin-bottom: 0.25rem;
        }

        .payment-method-info p {
          font-size: 0.875rem;
          color: var(--text-secondary, #64748b);
          margin: 0;
        }

        .payment-method-radio {
          margin-left: auto;
        }

        .payment-method-radio input[type="radio"] {
          display: none;
        }

        .radio-custom {
          width: 20px;
          height: 20px;
          border: 2px solid var(--border-color, #e2e8f0);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all var(--transition-fast);
        }

        .payment-method-radio input[type="radio"]:checked + .radio-custom {
          border-color: var(--primary-color, #2f4a67);
          background: var(--primary-color, #2f4a67);
        }

        .payment-method-radio input[type="radio"]:checked + .radio-custom::after {
          content: '';
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
        }

        .step-navigation {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          margin-top: 2rem;
        }

        .btn-primary, .btn-secondary {
          padding: 0.75rem 2rem;
          border: none;
          border-radius: var(--radius-lg, 0.75rem);
          font-weight: 600;
          cursor: pointer;
          transition: all var(--transition-fast);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-primary {
          background: var(--gradient-primary, linear-gradient(135deg, #2f4a67, #23394f));
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1));
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: var(--gray-200, #e2e8f0);
          color: var(--text-primary, #1e293b);
        }

        .btn-secondary:hover {
          background: var(--gray-300, #cbd5e1);
        }

        .btn-large {
          padding: 1rem 2rem;
          font-size: 1.125rem;
        }

        .order-summary-sidebar {
          background: var(--bg-primary, white);
          border-radius: var(--radius-2xl, 1.5rem);
          padding: 2rem;
          box-shadow: var(--shadow-lg, 0 10px 15px -3px rgb(0 0 0 / 0.1));
          height: fit-content;
          position: sticky;
          top: 100px;
        }

        .order-summary-sidebar h3 {
          color: var(--text-primary, #1e293b);
          margin-bottom: 1.5rem;
        }

        .summary-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .summary-item {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .summary-item img {
          width: 60px;
          height: 60px;
          border-radius: var(--radius-md, 0.5rem);
          object-fit: cover;
        }

        .summary-item-details {
          flex: 1;
        }

        .summary-item-details h4 {
          font-size: 0.875rem;
          color: var(--text-primary, #1e293b);
          margin-bottom: 0.25rem;
        }

        .summary-item-details p {
          font-size: 0.75rem;
          color: var(--text-secondary, #64748b);
          margin: 0;
        }

        .summary-item-price {
          font-weight: 600;
          color: var(--text-primary, #1e293b);
        }

        .price-breakdown {
          border-top: 1px solid var(--border-color, #e2e8f0);
          padding-top: 1rem;
          margin-bottom: 1.5rem;
        }

        .price-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .price-row.total {
          font-weight: 700;
          font-size: 1.125rem;
          color: var(--text-primary, #1e293b);
          padding-top: 0.75rem;
          border-top: 1px solid var(--border-color, #e2e8f0);
        }

        .secure-check {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: var(--success-color, #16a34a);
          font-weight: 600;
        }

        .coupon-section {
          background: var(--gray-50, #f8fafc);
          padding: 1.25rem;
          border-radius: var(--radius-lg, 0.75rem);
          margin-bottom: 2rem;
          border: 1px dashed var(--border-color, #e2e8f0);
        }

        .coupon-section h4 {
          margin: 0 0 1rem;
          font-size: 1rem;
          color: var(--text-primary, #1e293b);
        }

        .coupon-input-wrapper {
          display: flex;
          gap: 0.5rem;
        }

        .coupon-input-wrapper input {
          flex: 1;
          padding: 0.75rem;
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: var(--radius-md, 0.5rem);
          font-family: inherit;
          text-transform: uppercase;
        }

        .btn-apply-coupon {
          padding: 0.75rem 1.25rem;
          background: #232f3e;
          color: white;
          border: none;
          border-radius: var(--radius-md, 0.5rem);
          font-weight: 600;
          cursor: pointer;
        }

        .btn-remove-coupon {
          padding: 0.75rem 1.25rem;
          background: #fee2e2;
          color: #ef4444;
          border: none;
          border-radius: var(--radius-md, 0.5rem);
          font-weight: 600;
          cursor: pointer;
        }

        .applied-coupon-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.75rem;
          color: #16a34a;
          font-size: 0.875rem;
        }

        .price-row.discount {
          color: #16a34a;
          font-weight: 600;
        }

        .order-success {
          text-align: center;
          padding: 4rem 2rem;
        }

        .success-content {
          max-width: 500px;
          margin: 0 auto;
        }

        .success-icon {
          font-size: 4rem;
          color: var(--success-color, #16a34a);
          margin-bottom: 2rem;
        }

        .order-success h2 {
          color: var(--text-primary, #1e293b);
          margin-bottom: 1rem;
        }

        .order-success p {
          color: var(--text-secondary, #64748b);
          margin-bottom: 2rem;
        }

        .summary-section {
          margin-bottom: 2rem;
        }

        .summary-section h3 {
          color: var(--text-primary, #1e293b);
          margin-bottom: 1rem;
        }

        .selected-address {
          background: var(--gray-50, #f8fafc);
          padding: 1rem;
          border-radius: var(--radius-lg, 0.75rem);
        }

        .selected-address p {
          margin-bottom: 0.5rem;
          color: var(--text-secondary, #64748b);
        }

        .selected-payment {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: var(--gray-50, #f8fafc);
          padding: 1rem;
          border-radius: var(--radius-lg, 0.75rem);
        }

        .order-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .order-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--gray-50, #f8fafc);
          border-radius: var(--radius-lg, 0.75rem);
        }

        .order-item img {
          width: 60px;
          height: 60px;
          border-radius: var(--radius-md, 0.5rem);
          object-fit: cover;
        }

        .item-details {
          flex: 1;
        }

        .item-details h4 {
          font-size: 1rem;
          color: var(--text-primary, #1e293b);
          margin-bottom: 0.25rem;
        }

        .item-details p {
          font-size: 0.875rem;
          color: var(--text-secondary, #64748b);
          margin: 0;
        }

        .item-price {
          font-weight: 600;
          color: var(--text-primary, #1e293b);
        }

        @media (max-width: 1024px) {
          .payment-content {
            grid-template-columns: 1fr;
          }

          .order-summary-sidebar {
            position: static;
          }
        }

        @media (max-width: 768px) {
          .progress-indicator {
            gap: 1rem;
          }

          .step-label {
            font-size: 0.75rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .step-navigation {
            flex-direction: column;
          }

          .step-navigation button {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .payment-page {
            padding: 1rem 0;
          }

          .payment-header {
            margin-bottom: 1.5rem;
          }

          .payment-header h1 {
            font-size: 1.75rem;
          }

          .progress-indicator {
            gap: 0.5rem;
          }

          .step-number {
            width: 32px;
            height: 32px;
            font-size: 0.875rem;
          }

          .step-label {
            font-size: 0.7rem;
          }

          .payment-main {
            padding: 1rem;
            border-radius: 1rem;
          }

          .order-summary-sidebar {
            padding: 1rem;
            border-radius: 1rem;
          }

          .address-card {
            padding: 1rem;
            border-radius: 0.75rem;
          }

          .address-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .add-address-form {
            padding: 1rem;
            margin-top: 1rem;
          }

          .form-grid {
            gap: 1rem;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .form-actions button {
            width: 100%;
            justify-content: center;
          }

          .payment-method-card {
            padding: 1rem;
            border-radius: 0.75rem;
          }

          .payment-method-icon {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }

          .payment-method-info h3 {
            font-size: 1rem;
          }

          .payment-method-info p {
            font-size: 0.75rem;
          }

          .coupon-section {
            padding: 1rem;
          }

          .coupon-input-wrapper {
            flex-direction: column;
          }

          .coupon-input-wrapper button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Payment;
